/**
 * @p5doc module
 * @name utils/function
 *
 * @description
 * # Function utils
 *
 * Contains function-related utils:
 *
 * - throttle (borrowed from lodash)
 * - debounce (borrowed from lodash)
 */

/**
 * TODO: move to "type.js" module
 * @description
 * Determines if a reference is a `Function`.
 *
 * @param {*} value - Reference to check.
 * @returns {boolean} True if `value` is a `Function`.
 */
function isFunction(value) {
  return typeof value === "function";
}

/**
 * TODO: move to "type.js" module
 * @description
 * Determines if a reference is an `Object`. Unlike `typeof` in JavaScript, `null`s are not
 * considered to be objects. Note that JavaScript arrays are objects.
 *
 * @param {*} value - Reference to check.
 * @returns {boolean} True if `value` is an `Object` but not `null`.
 */
function isObject(value) {
  return value !== null && typeof value === "object";
}

/**
 * @description
 * Creates a function that delays invoking `func` until after `wait` milliseconds
 * have elapsed since the last time it was invoked. The created function comes
 * with a `cancel` method to cancel delayed invocations. Provide an options
 * object to indicate that `func` should be invoked on the leading and/or
 * trailing edge of the `wait` timeout. Subsequent calls to the debounced
 * function return the result of the last `func` invocation.
 *
 * **Note:** If `leading` and `trailing` options are `true`, `func` is invoked
 * on the trailing edge of the timeout only if the the debounced function is
 * invoked more than once during the `wait` timeout.
 *
 * See [David Corbacho's article](http://drupalmotion.com/article/debounce-and-throttle-visual-explanation)
 * for details over the differences between `debounce` and `throttle`.
 *
 * @param {Function} func The function to debounce.
 * @param {number} [wait=0] The number of milliseconds to delay.
 * @param {Object} [options] The options object.
 * @param {boolean} [options.leading=false] Specify invoking on the leading edge of the timeout.
 * @param {number} [options.maxWait] The maximum time `func` is allowed to be delayed before it is invoked.
 * @param {boolean} [options.trailing=true] Specify invoking on the trailing edge of the timeout.
 *
 * @returns {Function} Returns the new debounced function.
 */
export function debounce(func, wait, options) {
  var args,
    maxTimeoutId,
    result,
    stamp,
    thisArg,
    timeoutId,
    trailingCall,
    lastCalled = 0,
    maxWait = false,
    trailing = true;

  if(!isFunction(func)) {
    throw new TypeError("Expected a function.");
  }
  wait = wait < 0 ? 0 : (+wait || 0);
  if(options === true) {
    var leading = true;
    trailing = false;
  } else if(isObject(options)) {
    leading = options.leading;
    maxWait = "maxWait" in options && Math.max(+options.maxWait || 0, wait);
    trailing = "trailing" in options ? options.trailing : trailing;
  }

  function cancel() {
    if(timeoutId) {
      clearTimeout(timeoutId);
    }
    if(maxTimeoutId) {
      clearTimeout(maxTimeoutId);
    }
    maxTimeoutId = timeoutId = trailingCall = undefined;
  }

  function delayed() {
    var remaining = wait - (Date.now() - stamp);
    if(remaining <= 0 || remaining > wait) {
      if(maxTimeoutId) {
        clearTimeout(maxTimeoutId);
      }
      var isCalled = trailingCall;
      maxTimeoutId = timeoutId = trailingCall = undefined;
      if(isCalled) {
        lastCalled = Date.now();
        result = func.apply(thisArg, args);
        if(!timeoutId && !maxTimeoutId) {
          args = thisArg = null;
        }
      }
    } else {
      timeoutId = setTimeout(delayed, remaining);
    }
  }

  function maxDelayed() {
    if(timeoutId) {
      clearTimeout(timeoutId);
    }
    maxTimeoutId = timeoutId = trailingCall = undefined;
    if(trailing || (maxWait !== wait)) {
      lastCalled = Date.now();
      result = func.apply(thisArg, args);
      if(!timeoutId && !maxTimeoutId) {
        args = thisArg = null;
      }
    }
  }

  function debounced() {
    args = arguments;
    stamp = Date.now();
    thisArg = this;
    trailingCall = trailing && (timeoutId || !leading);

    if(maxWait === false) {
      var leadingCall = leading && !timeoutId;
    } else {
      if(!maxTimeoutId && !leading) {
        lastCalled = stamp;
      }
      var remaining = maxWait - (stamp - lastCalled),
        isCalled = remaining <= 0 || remaining > maxWait;

      if(isCalled) {
        if(maxTimeoutId) {
          maxTimeoutId = clearTimeout(maxTimeoutId);
        }
        lastCalled = stamp;
        result = func.apply(thisArg, args);
      }
      else if(!maxTimeoutId) {
        maxTimeoutId = setTimeout(maxDelayed, remaining);
      }
    }
    if(isCalled && timeoutId) {
      timeoutId = clearTimeout(timeoutId);
    }
    else if(!timeoutId && wait !== maxWait) {
      timeoutId = setTimeout(delayed, wait);
    }
    if(leadingCall) {
      isCalled = true;
      result = func.apply(thisArg, args);
    }
    if(isCalled && !timeoutId && !maxTimeoutId) {
      args = thisArg = null;
    }
    return result;
  }

  debounced.cancel = cancel;
  return debounced;
}

/**
 * @description
 * Creates a function that only invokes `func` at most once per every `wait`
 * milliseconds. The created function comes with a `cancel` method to cancel
 * delayed invocations. Provide an options object to indicate that `func`
 * should be invoked on the leading and/or trailing edge of the `wait` timeout.
 * Subsequent calls to the throttled function return the result of the last
 * `func` call.
 *
 * **Note:** If `leading` and `trailing` options are `true`, `func` is invoked
 * on the trailing edge of the timeout only if the the throttled function is
 * invoked more than once during the `wait` timeout.
 *
 * See [David Corbacho's article](http://drupalmotion.com/article/debounce-and-throttle-visual-explanation)
 * for details over the differences between `throttle` and `debounce`.
 *
 * @param {Function} func The function to throttle.
 * @param {number} [wait=0] The number of milliseconds to throttle invocations to.
 * @param {Object} [options] The options object.
 * @param {boolean} [options.leading=true] Specify invoking on the leading edge of the timeout.
 * @param {boolean} [options.trailing=true] Specify invoking on the trailing edge of the timeout.
 *
 * @returns {Function} Returns the new throttled function.
 */
export function throttle(func, wait, options) {
  var leading = true;
  var trailing = true;

  if(!isFunction(func)) {
    throw new TypeError("Expected a function.");
  }

  if(options === false) {
    leading = false;
  } else if(isObject(options)) {
    leading = "leading" in options ? !!options.leading : leading;
    trailing = "trailing" in options ? !!options.trailing : trailing;
  }
  return debounce(func, wait, {
    leading,
    maxWait: +wait,
    trailing
  });
}
